/**
 * features/setup/ChildLinks.tsx
 * ------------------------------------------------------------------
 * "Child Links" — a read view of which children are linked to which
 * parent portal account(s), reverse-mapped from
 * users[].parentAccount.studentIds. Admins can surgically delink a
 * child from a parent (removeChildLink CF).
 *
 * The parent directory (users) is only streamed to admin/head, so for
 * other staff we show a notice instead of a misleading "not linked".
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Link2, Link2Off, Search, ShieldAlert, Loader2, AlertTriangle, KeyRound } from 'lucide-react';
import { functions } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';

interface DelinkResult { ok: boolean; noop?: boolean; orphaned?: boolean; keyAction?: string; reactivatedCode?: string | null }

export default function ChildLinks() {
  const { students, classes, users } = useData();
  const { userData } = useAuth();
  const isAdmin = useMemo(() => {
    const roles = userData?.roles ?? (userData?.role ? [userData.role] : []);
    return roles.some((r) => r === 'admin' || r === 'systemAdmin');
  }, [userData]);

  const classById = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);
  // studentId -> [{ uid, name, email }]
  const parentsByStudent = useMemo(() => {
    const m = new Map<string, { uid: string; name: string; email: string }[]>();
    for (const u of users) {
      const ids = u.parentAccount?.studentIds;
      if (!Array.isArray(ids)) continue;
      for (const sid of ids) {
        const arr = m.get(sid) ?? [];
        arr.push({ uid: u.uid, name: u.displayName || u.email || u.uid, email: u.email || '' });
        m.set(sid, arr);
      }
    }
    return m;
  }, [users]);

  const orphanAccounts = useMemo(
    () => users.filter((u) => u.parentAccount && (!(u.parentAccount.studentIds?.length))),
    [users],
  );

  const [q, setQ] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [confirm, setConfirm] = useState<{ studentId: string; studentName: string; parentUid: string; parentName: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return students
      .filter((s) => classFilter === 'all' || s.currentClass === classFilter)
      .filter((s) => !needle || s.fullName.toLowerCase().includes(needle) || (s.admissionNumber || '').toLowerCase().includes(needle))
      .map((s) => ({ s, parents: parentsByStudent.get(s.id) ?? [] }))
      .sort((a, b) => a.s.fullName.localeCompare(b.s.fullName));
  }, [students, classFilter, q, parentsByStudent]);

  const linkedCount = useMemo(() => rows.filter((r) => r.parents.length > 0).length, [rows]);
  const sortedClasses = useMemo(() => classes, [classes]);

  async function doDelink() {
    if (!confirm || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const fn = httpsCallable<{ parentUid: string; studentId: string }, DelinkResult>(functions, 'removeChildLink');
      const { data } = await fn({ parentUid: confirm.parentUid, studentId: confirm.studentId });
      const parts = [`${confirm.studentName} delinked from ${confirm.parentName}.`];
      if (data.orphaned) parts.push('That parent now has no children — flagged for review.');
      if (data.keyAction === 'reactivated' && data.reactivatedCode) parts.push(`Registration code ${data.reactivatedCode} is active again for re-linking.`);
      else if (data.keyAction === 'already-unused') parts.push('An unused registration code is already available for re-linking.');
      setMsg({ ok: true, text: parts.join(' ') });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  if (users.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
        <ShieldAlert size={18} className="text-amber-500 mt-0.5 shrink-0" />
        <span>Parent-link details require <strong>admin or headteacher</strong> access. Ask an administrator to review child links.</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary + filters */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600 dark:text-gray-300"><strong className="text-gray-900 dark:text-white">{linkedCount}</strong> linked</span>
            <span className="text-gray-600 dark:text-gray-300"><strong className="text-gray-900 dark:text-white">{rows.length - linkedCount}</strong> not linked</span>
            <span className="text-gray-400">of {rows.length} shown</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / adm no…"
                className="w-full sm:w-56 pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white">
              <option value="all">All classes</option>
              {sortedClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        {orphanAccounts.length > 0 && (
          <div className="mt-3 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <AlertTriangle size={13} /> {orphanAccounts.length} parent account{orphanAccounts.length === 1 ? '' : 's'} with no linked children — review whether to keep or remove.
          </div>
        )}
      </div>

      {msg && (
        <div className={`text-sm rounded-xl px-4 py-3 ${msg.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
          {msg.text}
        </div>
      )}

      {/* List */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/60">
        {rows.length === 0 ? (
          <p className="p-5 text-sm text-gray-500 dark:text-gray-400">No students match.</p>
        ) : (
          rows.slice(0, 400).map(({ s, parents }) => (
            <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="sm:w-64 shrink-0">
                <div className="font-medium text-gray-900 dark:text-white">{s.fullName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {classById.get(s.currentClass)?.name ?? s.currentClass}{s.admissionNumber ? ` · ${s.admissionNumber}` : ''}
                </div>
              </div>
              {parents.length === 0 ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300 w-fit">
                  <Link2Off size={13} /> Not linked
                </span>
              ) : (
                <div className="flex-1 flex flex-wrap gap-2">
                  {parents.map((p) => (
                    <span key={p.uid} className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/25 text-emerald-800 dark:text-emerald-200 text-xs">
                      <Link2 size={12} />
                      <span className="font-medium">{p.name}</span>
                      {p.email && <span className="text-emerald-600/70 dark:text-emerald-300/70">{p.email}</span>}
                      {isAdmin && (
                        <button
                          onClick={() => setConfirm({ studentId: s.id, studentName: s.fullName, parentUid: p.uid, parentName: p.name })}
                          title="Delink this child from this parent"
                          className="ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 text-red-500"
                        >
                          <Link2Off size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {rows.length > 400 && <p className="text-xs text-gray-400">Showing 400 of {rows.length} — narrow with search or class filter.</p>}

      {/* Confirm delink */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl p-6">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-300 mb-2">
              <Link2Off size={18} /> <h3 className="font-bold">Delink child?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Delink <strong>{confirm.studentName}</strong> from <strong>{confirm.parentName}</strong>? They will lose access
              to this child's reports, marks and attendance. The child's registration code is reactivated so they can be
              re-linked later.
            </p>
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
              <KeyRound size={12} /> The parent's other children and any staff roles are kept. Access drops on their next token refresh.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setConfirm(null)} disabled={busy} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40">Cancel</button>
              <button onClick={doDelink} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Link2Off size={15} />} Delink
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
